/**
 * /reembolsos — Política de reembolsos.
 *
 * Required by Culqi as part of onboarding. Spells out: what's refundable,
 * the time window, and the process to request a refund. The wording is
 * intentionally generous on the first 7 days so a user that buys by
 * mistake can recover.
 */

import { LegalSection, LegalShell } from '@/components/layout/LegalShell';
import { contact } from '@/lib/contact';

export function RefundsPage() {
  return (
    <LegalShell eyebrow="Legal" title="Política de reembolsos" updated="Última actualización: mayo de 2026">
      <LegalSection title="Resumen rápido">
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Packs de fierros</strong>: reembolsables al 100% si no has usado ningún fierro,
            dentro de los 7 días posteriores a la compra.
          </li>
          <li>
            <strong>Suscripciones</strong>: reembolsables al 100% si las cancelas dentro de las
            primeras 48 horas y no has hecho exportaciones.
          </li>
          <li>
            <strong>Bono mensual gratuito</strong>: no aplica (no fue pagado).
          </li>
          <li>
            Si tu suscripción ya tiene actividad o ha pasado el plazo, te ofrecemos crédito en
            fierros como compensación parcial.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="1. Packs de fierros (compra única)">
        <p>
          Si compraste un pack de 50 o 150 fierros y aún <strong>no has consumido ninguno</strong>,
          puedes solicitar el reembolso íntegro dentro de los <strong>7 días calendario</strong>{' '}
          posteriores al cargo.
        </p>
        <p>
          Si ya consumiste parte del pack, te reembolsamos proporcionalmente los fierros no
          utilizados, descontando los ya consumidos al precio unitario del plan.
        </p>
      </LegalSection>

      <LegalSection title="2. Suscripciones (mensual o anual)">
        <p>
          Si cancelas dentro de las <strong>48 horas</strong> posteriores al cargo y{' '}
          <strong>no has realizado ninguna exportación</strong> en ese periodo, te reembolsamos al
          100%.
        </p>
        <p>
          Después de las 48 horas o si ya hiciste exportaciones, la suscripción continuará activa
          hasta el final del periodo pagado y no será renovada automáticamente. No emitimos
          reembolsos prorrateados, pero puedes seguir usando el servicio hasta el vencimiento.
        </p>
      </LegalSection>

      <LegalSection title="3. Cargos duplicados o no autorizados">
        <p>
          Si detectaste un cargo que no reconoces, escríbenos de inmediato con el identificador
          Culqi (visible en tu historial de compras). Procesamos la reversión en un máximo de{' '}
          <strong>5 días hábiles</strong>.
        </p>
      </LegalSection>

      <LegalSection title="4. Cómo solicitar un reembolso">
        <ol className="list-decimal space-y-1 pl-5">
          <li>
            Envía un correo a{' '}
            <a
              href={`mailto:${contact.refundsEmail}`}
              className="underline-offset-2 hover:underline"
              style={{ color: 'var(--accent)' }}
            >
              {contact.refundsEmail}
            </a>
            .
          </li>
          <li>Incluye tu correo de la cuenta y el ID Culqi del cargo (formato <code className="font-mono">ch_xxx</code>).</li>
          <li>Indica el motivo del reembolso.</li>
        </ol>
        <p>
          Te confirmaremos la recepción en menos de <strong>48 horas</strong>. El crédito en tu
          tarjeta puede tardar entre 5 y 15 días hábiles según el banco emisor.
        </p>
      </LegalSection>

      <LegalSection title="5. Casos no reembolsables">
        <ul className="list-disc space-y-1 pl-5">
          <li>Fierros ya consumidos en exportaciones.</li>
          <li>Compras realizadas hace más de 30 días.</li>
          <li>Cuentas suspendidas por violación de los términos.</li>
        </ul>
      </LegalSection>

      <LegalSection title="6. Alternativas">
        <p>
          Si no calificas para reembolso pero sientes que el servicio no cumplió lo prometido,
          podemos ofrecerte: (a) crédito en fierros, (b) extensión de tu suscripción, o (c) cambio
          de plan. Cuéntanos qué pasó y buscaremos una solución justa.
        </p>
      </LegalSection>

      <LegalSection title="7. Indecopi">
        <p>
          Si no llegamos a un acuerdo, puedes presentar tu reclamo formal en nuestro{' '}
          <a href="/reclamaciones" className="underline-offset-2 hover:underline" style={{ color: 'var(--accent)' }}>
            Libro de Reclamaciones
          </a>{' '}
          o recurrir directamente a Indecopi.
        </p>
      </LegalSection>
    </LegalShell>
  );
}
